import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { getPostAuthRedirect } from '../../../services/whopPlanService';

const LoginForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;

      // Successfully logged in - route based on returnUrl or Whop plan
      if (data?.user) {
        const returnUrl = searchParams.get('returnUrl');
        
        if (returnUrl) {
          // If there's a return URL, go there
          navigate(returnUrl);
        } else {
          // Fetch user profile to check onboarding status
          try {
            const { data: profileData } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', data.user.id)
              .single();
            
            const redirect = await getPostAuthRedirect(data.user.email, profileData);
            navigate(redirect);
          } catch (err) {
            console.warn('Failed to determine redirect, using dashboard', err);
            navigate('/app/dashboard');
          }
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({
        general: error?.message || 'Invalid email or password. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        type="email"
        name="email"
        label="Email Address"
        placeholder="Enter your email"
        value={formData.email}
        onChange={handleChange}
        error={errors?.email}
        required
        autoFocus
      />
      
      <Input
        type={showPassword ? 'text' : 'password'}
        name="password"
        label="Password"
        placeholder="Enter your password"
        value={formData.password}
        onChange={handleChange}
        error={errors?.password}
        required
        rightElement={
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        }
      />
      
      {errors?.general && (
        <div className="text-sm text-red-600 p-2 bg-red-50 rounded">
          {errors?.general}
        </div>
      )}
      
      <Button
        type="submit"
        variant="default"
        loading={loading}
        fullWidth
        iconName="LogIn"
        iconPosition="right"
      >
        Sign In
      </Button>
      
      <div className="text-center">
        <button
          type="button"
          onClick={() => navigate('/reset-password')}
          className="text-sm text-primary hover:text-secondary font-medium"
        >
          Forgot your password?
        </button>
      </div>

      <div className="mt-4 p-4 bg-accent/10 rounded-lg border border-accent/20">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={18} className="text-primary mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-semibold text-foreground mb-1">Secure Login</p>
            <p>Your credentials are encrypted and protected with industry-standard security.</p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default LoginForm;
